import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import { fetchJson } from '@/lib/fetch-json'
import { Section, useToast } from './_admin-utils'

interface Slot {
  id: string;
  slot_name: string;
  slot_starts: string;
  capacity: number;
}

interface Teacher {
  id: string;
  email: string;
  full_name?: string;
}

interface Pool {
  id: string;
  available: number;
}

interface RunStatusItem {
  loading: boolean;
  message: string | null;
  error: string | null;
}

interface RefreshOptions {
  signal?: AbortSignal;
  withLoading?: boolean;
}

const sortSlots = (items: Slot[] = []): Slot[] =>
  [...items].sort((a, b) => new Date(a.slot_starts).getTime() - new Date(b.slot_starts).getTime())

function useDebounced<T extends (...args: unknown[]) => unknown>(callback: T, delay = 350) {
  const latestCallback = useRef<T>(callback)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    latestCallback.current = callback
  }, [callback])

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => cancel, [cancel])

  const run = useCallback(
    (...args: unknown[]) => {
      cancel()
      return new Promise((resolve, reject) => {
        timerRef.current = setTimeout(async () => {
          try {
            resolve(await latestCallback.current(...args))
          } catch (error) {
            reject(error)
          }
        }, delay)
      })
    },
    [cancel, delay],
  )

  return useMemo(
    () => ({
      run,
      cancel,
    }),
    [run, cancel],
  )
}

export default function Randomizer() {
  const { session, profile } = useAuth()
  const { show, ToastHost } = useToast()

  const [slots, setSlots] = useState<Slot[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const [formName, setFormName] = useState<string>('')
  const [formDate, setFormDate] = useState<string>('')
  const [formCapacity, setFormCapacity] = useState<number | string>(1)
  const [creating, setCreating] = useState<boolean>(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [runStatus, setRunStatus] = useState<Record<string, RunStatusItem>>({})
  const [assignedMessage, setAssignedMessage] = useState<string | null>(null)
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [teacherLoading, setTeacherLoading] = useState<boolean>(false)
  const [teacherError, setTeacherError] = useState<string>('')
  const [pools, setPools] = useState<Pool[]>([])
  const [poolLoading, setPoolLoading] = useState<boolean>(false)
  const [poolError, setPoolError] = useState<string>('')
  const [mintTeacher, setMintTeacher] = useState<string>('')
  const [mintPool, setMintPool] = useState<string>('')
  const [minting, setMinting] = useState<boolean>(false)
  const [mintError, setMintError] = useState<string>('')

  const token = session?.access_token ?? null
  const initialLoadRef = useRef(false)

  const refreshPools = useCallback(
    async ({ signal, withLoading = false }: RefreshOptions = {}) => {
      if (!token) return []

      if (withLoading) {
        setPoolLoading(true)
        setPoolError('')
      }

      try {
        const request = fetchJson('/api/v1/admin/randomizer/pools', token, signal ? { signal } : {})
        const payload = await request.promise as { data?: { pools?: Pool[] }; pools?: Pool[] }
        const items = Array.isArray(payload?.data?.pools) ? payload.data.pools : Array.isArray(payload?.pools) ? payload.pools : []
        if (signal?.aborted) return items
        setPools(items)
        return items
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') return []
        setPoolError((error as Error)?.message || 'Failed to load pools')
        setPools([])
        throw error
      } finally {
        if (withLoading && !signal?.aborted) {
          setPoolLoading(false)
        }
      }
    },
    [token],
  )

  const refreshSlots = useCallback(
    async ({ signal, withLoading = false }: RefreshOptions = {}) => {
      if (!token) return []

      if (withLoading) {
        setLoading(true)
        setError(null)
      }

      try {
        const request = fetchJson('/api/v1/admin/randomizer/slots', token, signal ? { signal } : {})
        const payload = await request.promise as { data?: { items?: Slot[] }; items?: Slot[] }
        const items = sortSlots(Array.isArray(payload?.data?.items) ? payload.data.items : Array.isArray(payload?.items) ? payload.items : [])
        if (signal?.aborted) return items
        setSlots(items)
        return items
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') return []
        setError((error as Error)?.message || 'Failed to load slots')
        setSlots([])
        throw error
      } finally {
        if (withLoading && !signal?.aborted) {
          setLoading(false)
        }
      }
    },
    [token],
  )

  const refreshTeachers = useCallback(
    async ({ signal, withLoading = false }: RefreshOptions = {}) => {
      if (!token) return []

      if (withLoading) {
        setTeacherLoading(true)
        setTeacherError('')
      }

      try {
        const params = new URLSearchParams({ role: 'teacher', page: '1', pageSize: '200' })
        const request = fetchJson(
          `/api/v1/admin/users?${params.toString()}`,
          token,
          signal ? { signal } : {},
        )
        const payload = await request.promise as { data?: { items?: Teacher[] }; items?: Teacher[] }
        const items = Array.isArray(payload?.data?.items) ? payload.data.items : Array.isArray(payload?.items) ? payload.items : []
        if (signal?.aborted) return items
        setTeachers(items)
        return items
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') return []
        setTeacherError((error as Error)?.message || 'Failed to load teachers')
        setTeachers([])
        throw error
      } finally {
        if (withLoading && !signal?.aborted) {
          setTeacherLoading(false)
        }
      }
    },
    [token],
  )

  const { run: schedulePoolRefresh, cancel: cancelPoolRefresh } = useDebounced(refreshPools, 350)
  const { run: scheduleSlotRefresh, cancel: cancelSlotRefresh } = useDebounced(refreshSlots, 350)

  const isAdmin = ['admin', 'superadmin'].includes(profile?.role || '')
  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-cocoa/70">You do not have permission to view this page.</div>
  }

  useEffect(() => cancelPoolRefresh, [cancelPoolRefresh])
  useEffect(() => cancelSlotRefresh, [cancelSlotRefresh])

  useEffect(() => {
    if (!token) return
    if (initialLoadRef.current) return
    initialLoadRef.current = true

    const teacherController = new AbortController()
    const poolController = new AbortController()
    const slotController = new AbortController()

    refreshTeachers({ signal: teacherController.signal, withLoading: true }).catch((error) => {
      if ((error as Error)?.name === 'AbortError') return
    })

    refreshPools({ signal: poolController.signal, withLoading: true }).catch((error) => {
      if ((error as Error)?.name === 'AbortError') return
    })

    refreshSlots({ signal: slotController.signal, withLoading: true }).catch((error) => {
      if ((error as Error)?.name === 'AbortError') return
    })

    return () => {
      teacherController.abort()
      poolController.abort()
      slotController.abort()
    }
  }, [token, refreshTeachers, refreshPools, refreshSlots])

  useEffect(() => {
    if (!mintPool && pools.length === 1) {
      setMintPool(pools[0].id)
    }
  }, [pools, mintPool])

  useEffect(() => {
    if (mintPool && pools.every((pool) => pool.id !== mintPool)) {
      setMintPool('')
    }
  }, [pools, mintPool])

  useEffect(() => {
    if (mintTeacher && teachers.every((teacher) => teacher.id !== mintTeacher)) {
      setMintTeacher('')
    }
  }, [teachers, mintTeacher])

  const upcomingSlots = useMemo(() => sortSlots(slots), [slots])

  const handleMintSlot = async (event: React.FormEvent) => {
    event.preventDefault()
    setMintError('')

    if (!mintTeacher) {
      setMintError('Select a teacher')
      return
    }
    if (!mintPool) {
      setMintError('Select a pool')
      return
    }
    if (!token) {
      show('Not authenticated')
      return
    }

    setMinting(true)
    try {
      const request = fetchJson('/api/v1/admin/randomizer/slots/mint', token, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ teacher_id: mintTeacher, pool_id: mintPool }),
      })
      const data = await request.promise as { slot_id?: string }

      const slotId = data?.slot_id || ''
      if (slotId) {
        show(`Minted slot ${String(slotId).slice(0, 8)}…`)
      } else {
        show('Slot minted')
      }

      schedulePoolRefresh({ withLoading: true }).catch(() => {})
    } catch (err) {
      setMintError((err as Error)?.message || 'Unable to mint slot')
    } finally {
      setMinting(false)
    }
  }

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault()
    setFormError(null)

    if (!formName.trim()) {
      setFormError('Slot name is required')
      return
    }
    if (!formDate) {
      setFormError('Start time is required')
      return
    }
    const capacityNumber = Number.parseInt(String(formCapacity), 10)
    if (!Number.isFinite(capacityNumber) || capacityNumber < 1) {
      setFormError('Capacity must be at least 1')
      return
    }

    const parsedDate = new Date(formDate)
    if (Number.isNaN(parsedDate.getTime())) {
      setFormError('Start time is invalid')
      return
    }

    if (!token) {
      setFormError('Not authenticated')
      return
    }

    setCreating(true)
    try {
      const isoDate = parsedDate.toISOString()
      const request = fetchJson('/api/v1/admin/randomizer/slots', token, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          slot_name: formName.trim(),
          slot_starts: isoDate,
          capacity: capacityNumber,
        }),
      })

      const payload = await request.promise as { slot?: Slot }

      const created = payload?.slot
      if (created) {
        setSlots((prev) => sortSlots([...(prev || []), created]))
      } else {
        scheduleSlotRefresh({ withLoading: true }).catch(() => {})
      }
      setFormName('')
      setFormDate('')
      setFormCapacity(1)
    } catch (err) {
      setFormError((err as Error)?.message || 'Failed to create slot')
    } finally {
      setCreating(false)
    }
  }

  const handleRun = async (slotId: string) => {
    if (!token) return
    setRunStatus((prev) => ({
      ...prev,
      [slotId]: { loading: true, message: null, error: null },
    }))

    try {
      const request = fetchJson('/api/v1/admin/randomizer/run', token, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ slot_id: slotId }),
      })

      const data = await request.promise as { assigned?: number }

      const assigned = typeof data?.assigned === 'number' ? data.assigned : 0
      setAssignedMessage(`Assigned ${assigned} teacher${assigned === 1 ? '' : 's'}.`)
      setTimeout(() => setAssignedMessage(null), 2500)
      setRunStatus((prev) => ({
        ...prev,
        [slotId]: {
          loading: false,
          message: `Assigned ${assigned} teacher${assigned === 1 ? '' : 's'}.`,
          error: null,
        },
      }))
    } catch (err) {
      setRunStatus((prev) => ({
        ...prev,
        [slotId]: { loading: false, message: null, error: (err as Error)?.message || 'Randomizer run failed' },
      }))
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-6 min-w-0">
      <header>
        <h1 className="text-3xl font-semibold text-cocoa">Randomizer</h1>
        <p className="text-sm text-cocoa/70">
          Create assignment slots and randomly assign teachers up to each slot capacity.
        </p>
      </header>

      <Section title="Randomizer Controls" actions={assignedMessage ? <div className="text-sm text-mint font-medium">{assignedMessage}</div> : null}>
        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-cocoa">Mint slot to teacher</h2>
          <form
            onSubmit={handleMintSlot}
            className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end"
          >
            <label className="space-y-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-cocoa/60">Teacher</span>
              <select
                value={mintTeacher}
                onChange={(event) => {
                  setMintTeacher(event.target.value)
                  setMintError('')
                }}
                className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
              >
                <option value="">{teacherLoading ? 'Loading teachers…' : 'Select teacher'}</option>
                {teachers.map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.full_name ? `${teacher.full_name} • ${teacher.email}` : teacher.email}
                  </option>
                ))}
              </select>
              {teacherError ? <p className="text-xs text-red-600">{teacherError}</p> : null}
            </label>
            <label className="space-y-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-cocoa/60">Pool</span>
              <select
                value={mintPool}
                onChange={(event) => {
                  setMintPool(event.target.value)
                  setMintError('')
                }}
                className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
              >
                <option value="">{poolLoading ? 'Loading pools…' : 'Select pool'}</option>
                {pools.map((pool) => (
                  <option key={pool.id} value={pool.id} title={pool.id}>
                    {`${pool.id.slice(0, 8)}… (${pool.available} open)`}
                  </option>
                ))}
              </select>
              {poolError ? <p className="text-xs text-red-600">{poolError}</p> : null}
              {!poolLoading && pools.length === 0 ? (
                <p className="text-xs text-cocoa/60">No unassigned slots available.</p>
              ) : null}
            </label>
            <div className="flex flex-col gap-2 md:self-end md:items-end">
              <button
                type="submit"
                disabled={minting}
                className="rounded-full bg-terracotta px-4 py-2 text-sm font-semibold text-white shadow disabled:opacity-60"
              >
                {minting ? 'Minting…' : 'Mint slot'}
              </button>
            </div>
            <p className="text-xs text-cocoa/60 md:col-span-3">
              Sponsor payments automatically mint slots into the pools list. Minting here assigns that slot credit to the
              selected teacher without revealing classroom details back to the sponsor.
            </p>
            {mintError ? <div className="md:col-span-3 text-sm text-red-600">{mintError}</div> : null}
          </form>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-cocoa">Create slot</h2>
          <form onSubmit={handleCreate} className="grid gap-4 md:grid-cols-3">
            <label className="space-y-1 md:col-span-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-cocoa/60">Name</span>
              <input
                type="text"
                value={formName}
                onChange={(event) => setFormName(event.target.value)}
                className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 focus:border-terracotta focus:outline-none"
                placeholder="Fall Cohort"
              />
            </label>
            <label className="space-y-1 md:col-span-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-cocoa/60">Start</span>
              <input
                type="datetime-local"
                value={formDate}
                onChange={(event) => setFormDate(event.target.value)}
                className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 focus:border-terracotta focus:outline-none"
              />
            </label>
            <label className="space-y-1 md:col-span-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-cocoa/60">Capacity</span>
              <input
                type="number"
                min="1"
                value={formCapacity}
                onChange={(event) => setFormCapacity(Number(event.target.value) || 1)}
                className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 focus:border-terracotta focus:outline-none"
              />
            </label>
            <div className="md:col-span-3 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={creating}
                className="rounded-full bg-terracotta px-4 py-2 text-sm font-semibold text-white shadow disabled:opacity-60"
              >
                {creating ? 'Creating…' : 'Create slot'}
              </button>
              {formError ? <div className="text-sm text-red-600">{formError}</div> : null}
            </div>
          </form>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-cocoa">Upcoming slots</h2>
          <div className="overflow-hidden rounded-xl border border-white/70">
            <table className="min-w-full divide-y divide-white/60 text-sm">
              <thead className="bg-shell/60 text-cocoa/70">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Slot</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Starts</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Capacity</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/60 bg-white/50">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-cocoa/60">
                      Loading slots…
                    </td>
                  </tr>
                ) : upcomingSlots.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-cocoa/60">
                      No slots yet. Create one to get started.
                    </td>
                  </tr>
                ) : (
                  upcomingSlots.map((slot) => {
                    const state = runStatus[slot.id] || { loading: false, message: null, error: null }
                    return (
                      <tr key={slot.id} className="align-top hover:bg-shell/40 transition-colors">
                        <td className="px-4 py-3 font-medium text-cocoa/90">{slot.slot_name}</td>
                        <td className="px-4 py-3 text-cocoa/80">
                          {slot.slot_starts ? new Date(slot.slot_starts).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-cocoa/80">{slot.capacity}</td>
                        <td className="px-4 py-3 space-y-2">
                          <button
                            type="button"
                            onClick={() => handleRun(slot.id)}
                            disabled={state.loading}
                            className="rounded-full bg-terracotta px-4 py-2 text-sm font-semibold text-white shadow disabled:opacity-60"
                          >
                            {state.loading ? 'Running…' : 'Run now'}
                          </button>
                          {state.message ? (
                            <div className="text-xs text-emerald-600">{state.message}</div>
                          ) : null}
                          {state.error ? <div className="text-xs text-red-600">{state.error}</div> : null}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {error ? <div className="text-sm text-red-600">{error}</div> : null}
        </section>
      </Section>

      <ToastHost />
    </div>
  )
}
